import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Trophy } from 'lucide-react';

const PERIODS = [
  { key: 'day', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'year', label: 'This Year' },
];

// Shared by Dashboard.js (sanctuary-wide, guildId omitted) and GroupHub.js
// (guild-scoped, guildId passed in) -- same leaderboard shape, just a
// different scope_type/guild_id filter against book_favorites_leaderboard.
const SanctuaryFavorites = ({ guildId }) => {
  const [period, setPeriod] = useState('week');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('book_favorites_leaderboard')
      .select('isbn,title,author,cover_url,win_count,total_duels,win_rate,period_start')
      .eq('period_type', period)
      .order('win_count', { ascending: false })
      .limit(10);

    query = guildId ? query.eq('scope_type', 'guild').eq('guild_id', guildId) : query.eq('scope_type', 'sanctuary');

    const { data, error } = await query;
    setRows(error ? [] : (data || []));
    setLoading(false);
  }, [period, guildId]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const periodLabel = PERIODS.find(p => p.key === period)?.label || period;

  return (
    <div style={containerStyle}>
      <div style={headerRow}>
        <Trophy size={18} color="#ffc107" />
        <h4 style={headerTitle}>{guildId ? "The Guild's Favorites" : "The Sanctuary's Favorites"}</h4>
      </div>

      <div style={tabRow}>
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            style={period === p.key ? activePeriodBtn : periodBtn}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={emptyText}>Counting votes...</p>
      ) : rows.length === 0 ? (
        <p style={emptyText}>No duels logged for {periodLabel.toLowerCase()} yet -- head to The Favorites Garden to compare some books!</p>
      ) : (
        <div style={listStyle}>
          {rows.map((row, idx) => (
            <div key={row.isbn} style={rowStyle}>
              <div style={rankBadge}>{idx + 1}</div>
              {row.cover_url ? (
                <img src={row.cover_url} alt="" style={coverStyle} />
              ) : (
                <div style={placeholderCoverStyle}>{row.title?.[0] || '?'}</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={titleStyle}>{row.title}</div>
                <div style={authorStyle}>{row.author}</div>
              </div>
              <div style={winStatStyle}>
                <div style={winCountStyle}>{row.win_count}</div>
                <div style={winLabelStyle}>of {row.total_duels} duels</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Styles -- deliberately mirrors TrophyFeed.js's visual language (same
// var(--*) tokens) so this reads as a sibling feature, not a new one.
const containerStyle = { marginTop: '1.5rem' };
const headerRow = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' };
const headerTitle = { margin: 0, fontSize: '1.1rem', color: 'var(--text-main)' };

const tabRow = { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' };
const periodBtn = {
  padding: '6px 12px', borderRadius: '20px', border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)', color: 'var(--text-soft)', fontSize: '0.75rem',
  fontWeight: 'bold', cursor: 'pointer',
};
const activePeriodBtn = { ...periodBtn, backgroundColor: 'var(--accent)', color: 'white', borderColor: 'var(--accent)' };

const emptyText = { fontSize: '0.85rem', color: 'var(--text-soft)' };

const listStyle = { display: 'flex', flexDirection: 'column', gap: '8px' };
const rowStyle = {
  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px',
  backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px',
};
const rankBadge = {
  width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold',
  backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', flexShrink: 0,
};
const coverStyle = { width: '40px', height: '60px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 };
const placeholderCoverStyle = {
  width: '40px', height: '60px', borderRadius: '4px', flexShrink: 0,
  backgroundColor: 'var(--bg-app)', border: '1px solid var(--border)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', color: 'var(--text-soft)', fontWeight: 'bold',
};
const titleStyle = { fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-main)', lineHeight: '1.2' };
const authorStyle = { fontSize: '0.72rem', color: 'var(--text-soft)' };
const winStatStyle = { textAlign: 'right', flexShrink: 0 };
const winCountStyle = { fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--accent)' };
const winLabelStyle = { fontSize: '0.65rem', color: 'var(--text-soft)' };

export default SanctuaryFavorites;
